import { useController } from "react-hook-form";
import type { Control, FieldPath, FieldValues } from "react-hook-form";
import type { ChangeEvent } from "react";
import { Field } from "../../ui";
import type { FieldElement, FieldProps } from "../../ui";

type ManagedFieldProp = "defaultValue" | "name" | "onBlur" | "onChange" | "ref" | "value";
type DistributiveOmit<T, K extends PropertyKey> = T extends unknown ? Omit<T, Extract<keyof T, K>> : never;

export type HookFormFieldProps<TFieldValues extends FieldValues> =
  DistributiveOmit<FieldProps, ManagedFieldProp> & {
    readonly formControl: Control<TFieldValues>;
    readonly name: FieldPath<TFieldValues>;
  };

export function HookFormField<TFieldValues extends FieldValues>({
  formControl,
  name,
  ...fieldProps
}: HookFormFieldProps<TFieldValues>) {
  const { field, fieldState } = useController({ control: formControl, name });
  const value = field.value == null ? "" : field.value as string | number | readonly string[];
  const error = fieldState.error?.message ?? fieldProps.error;
  if (fieldProps.control === "textarea") {
    return (
      <Field
        {...fieldProps}
        error={error}
        name={field.name}
        onBlur={field.onBlur}
        onChange={(event: ChangeEvent<HTMLTextAreaElement>) => field.onChange(event.currentTarget.value)}
        ref={field.ref as (instance: FieldElement | null) => void}
        value={value}
      />
    );
  }
  if (fieldProps.control === "select") {
    return (
      <Field
        {...fieldProps}
        error={error}
        name={field.name}
        onBlur={field.onBlur}
        onChange={(event: ChangeEvent<HTMLSelectElement>) => field.onChange(event.currentTarget.value)}
        ref={field.ref as (instance: FieldElement | null) => void}
        value={value}
      />
    );
  }
  return (
    <Field
      {...fieldProps}
      error={error}
      name={field.name}
      onBlur={field.onBlur}
      onChange={(event: ChangeEvent<HTMLInputElement>) => field.onChange(event.currentTarget.value)}
      ref={field.ref as (instance: FieldElement | null) => void}
      value={value}
    />
  );
}
